import React, { useEffect, useRef } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Node, Extension, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Plugin } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { PLACEHOLDER_RE } from '../utils/placeholders';
import { FiBold, FiItalic, FiList, FiLink, FiGrid, FiPlus, FiMinus as FiMinusIcon } from 'react-icons/fi';

/**
 * Shades [Insert ...] placeholders so an unfilled one is hard to miss.
 *
 * Decoration-only: nothing is written into body_html, so a placeholder that
 * survives to print appears as ordinary text rather than as shaded markup.
 */
const PlaceholderHighlight = Extension.create({
  name: 'placeholderHighlight',
  addProseMirrorPlugins() {
    return [
      new Plugin({
        props: {
          decorations(state) {
            const decos = [];
            state.doc.descendants((node, pos) => {
              if (!node.isText || !node.text) return;
              for (const m of node.text.matchAll(PLACEHOLDER_RE)) {
                decos.push(Decoration.inline(
                  pos + m.index,
                  pos + m.index + m[0].length,
                  { class: 'letter-placeholder' },
                ));
              }
            });
            return DecorationSet.create(state.doc, decos);
          },
        },
      }),
    ];
  },
});

/**
 * Block node marking where an attached photo prints inside the letter body.
 *
 * It serialises to `<div data-letter-photo="12"></div>` — a marker the backend
 * swaps for the real figure at render time. The image itself is never stored in
 * body_html, so editing a caption in the photo panel updates the letter without
 * touching the body.
 */
const LetterPhotoNode = Node.create({
  name: 'letterPhoto',
  group: 'block',
  atom: true,
  draggable: true,
  selectable: true,

  addAttributes() {
    return {
      photoId: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-letter-photo'),
        renderHTML: (attrs) =>
          (attrs.photoId ? { 'data-letter-photo': String(attrs.photoId) } : {}),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-letter-photo]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes)];
  },

  addCommands() {
    return {
      insertLetterPhoto: (photoId) => ({ commands }) =>
        commands.insertContent({
          type: 'letterPhoto',
          attrs: { photoId: String(photoId) },
        }),
    };
  },

  addNodeView() {
    // Plain DOM rather than a React node view: the card only reads from a ref
    // the parent keeps current, so there is no React tree to reconcile.
    return ({ node, editor }) => {
      const dom = document.createElement('div');
      dom.className = 'letter-photo-node';
      dom.contentEditable = 'false';
      dom.style.cssText =
        'margin:12px 0;padding:8px;border:1px solid #d1d5db;border-radius:8px;' +
        'background:#f9fafb;display:flex;gap:10px;align-items:center;max-width:420px;';

      const render = () => {
        const lookup = editor?.options?.letterPhotosRef?.current || {};
        const photo = lookup[String(node.attrs.photoId)];
        dom.innerHTML = '';

        const thumb = document.createElement('div');
        thumb.style.cssText =
          'width:78px;height:58px;flex:0 0 78px;border-radius:4px;overflow:hidden;' +
          'background:#e5e7eb;display:flex;align-items:center;justify-content:center;' +
          'font-size:20px;color:#9ca3af;';
        if (photo?.previewUrl) {
          const img = document.createElement('img');
          img.src = photo.previewUrl;
          img.alt = '';
          img.style.cssText = 'width:100%;height:100%;object-fit:cover;display:block;';
          thumb.appendChild(img);
        } else {
          thumb.textContent = photo ? '🖼' : '⚠';
        }

        const meta = document.createElement('div');
        meta.style.cssText = 'min-width:0;font-size:12px;line-height:1.4;';
        if (photo) {
          meta.innerHTML =
            '<div style="font-weight:600;color:#374151;">Photo in letter</div>' +
            '<div style="color:#6b7280;overflow:hidden;text-overflow:ellipsis;' +
            'display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;"></div>';
          meta.lastChild.textContent = photo.caption || 'No caption yet';
        } else {
          meta.innerHTML =
            '<div style="font-weight:600;color:#b45309;">Photo removed</div>' +
            '<div style="color:#6b7280;">Delete this block, or re-upload the photo.</div>';
        }

        dom.appendChild(thumb);
        dom.appendChild(meta);
      };

      render();
      return {
        dom,
        update: (updatedNode) => {
          if (updatedNode.type.name !== 'letterPhoto') return false;
          node = updatedNode;
          render();
          return true;
        },
      };
    };
  },
});

/**
 * Rich-text editor for the letter body. Receives `value` (HTML string) and emits
 * onChange(html) whenever the document changes.
 *
 * `photos` is the letter's attached photos; inline photo blocks read their
 * thumbnail and caption from it. `onReady` hands the editor instance back so the
 * page can insert a photo at the cursor.
 */
function LetterEditor({ value, onChange, placeholder, photos, onReady }) {
  // A ref, not a prop, so caption edits repaint the inline cards without
  // tearing down and rebuilding the editor.
  const photosRef = useRef({});
  photosRef.current = (photos || []).reduce((acc, p) => {
    acc[String(p.id)] = p;
    return acc;
  }, {});

  const editor = useEditor({
    extensions: [
      StarterKit.configure({}),
      Link.configure({ openOnClick: false, autolink: true }),
      LetterPhotoNode,
      PlaceholderHighlight,
      // resizable:false — column widths are decided by the print layout, and a
      // hand-dragged width would not survive the letter's fixed table-layout.
      Table.configure({ resizable: false }),
      TableRow,
      TableHeader,
      TableCell,
    ],
    content: value || '',
    letterPhotosRef: photosRef,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
    editorProps: {
      attributes: {
        style: 'min-height: 320px; padding: 16px; outline: none; font-size: 14px; line-height: 1.6;',
      },
    },
  });

  useEffect(() => {
    if (editor && onReady) onReady(editor);
  }, [editor, onReady]);

  // Repaint inline photo cards when a caption changes or a photo is deleted.
  useEffect(() => {
    if (!editor) return;
    editor.view.dispatch(editor.state.tr.setMeta('letterPhotosUpdated', true));
  }, [photos, editor]);

  // Keep editor in sync if `value` is changed externally (e.g. loading a draft)
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || '') !== current) {
      editor.commands.setContent(value || '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  const btn = (active) => ({
    padding: '6px 10px',
    background: active ? '#2563eb' : 'white',
    color: active ? 'white' : '#374151',
    border: '1px solid #e5e7eb',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '13px',
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  });

  const addLink = () => {
    const previousUrl = editor.getAttributes('link').href;
    const url = window.prompt('URL', previousUrl || 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  return (
    <div style={{ border: '1px solid #d1d5db', borderRadius: '8px', overflow: 'hidden', background: 'white' }}>
      <div style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        padding: '8px',
        borderBottom: '1px solid #e5e7eb',
        background: '#f9fafb',
      }}>
        <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} style={btn(editor.isActive('bold'))} title="Bold">
          <FiBold size={14} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} style={btn(editor.isActive('italic'))} title="Italic">
          <FiItalic size={14} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleStrike().run()} style={btn(editor.isActive('strike'))} title="Strikethrough">
          S
        </button>
        <div style={{ width: '1px', background: '#e5e7eb', margin: '0 4px' }} />
        <button type="button" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} style={btn(editor.isActive('heading', { level: 2 }))} title="Heading">
          H2
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleBulletList().run()} style={btn(editor.isActive('bulletList'))} title="Bulleted list">
          <FiList size={14} />
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleOrderedList().run()} style={btn(editor.isActive('orderedList'))} title="Numbered list">
          1.
        </button>
        <button type="button" onClick={() => editor.chain().focus().toggleBlockquote().run()} style={btn(editor.isActive('blockquote'))} title="Quote">
          &ldquo; &rdquo;
        </button>
        <div style={{ width: '1px', background: '#e5e7eb', margin: '0 4px' }} />
        <button
          type="button"
          onClick={() => editor.chain().focus()
            .insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}
          style={btn(false)}
          title="Insert table"
        >
          <FiGrid size={14} />
        </button>
        {editor.isActive('table') && (
          <>
            <button type="button" onClick={() => editor.chain().focus().addRowAfter().run()}
              style={btn(false)} title="Add row below"><FiPlus size={12} /> Row</button>
            <button type="button" onClick={() => editor.chain().focus().addColumnAfter().run()}
              style={btn(false)} title="Add column right"><FiPlus size={12} /> Col</button>
            <button type="button" onClick={() => editor.chain().focus().deleteRow().run()}
              style={btn(false)} title="Delete current row"><FiMinusIcon size={12} /> Row</button>
            <button type="button" onClick={() => editor.chain().focus().deleteColumn().run()}
              style={btn(false)} title="Delete current column"><FiMinusIcon size={12} /> Col</button>
            <button type="button" onClick={() => editor.chain().focus().toggleHeaderRow().run()}
              style={btn(false)} title="Toggle header row">Hdr</button>
            <button
              type="button"
              onClick={() => {
                if (window.confirm('Delete this entire table?')) {
                  editor.chain().focus().deleteTable().run();
                }
              }}
              style={{ ...btn(false), color: '#dc2626' }}
              title="Delete table"
            >
              Del table
            </button>
          </>
        )}
        <div style={{ width: '1px', background: '#e5e7eb', margin: '0 4px' }} />
        <button type="button" onClick={addLink} style={btn(editor.isActive('link'))} title="Link">
          <FiLink size={14} />
        </button>
        <div style={{ width: '1px', background: '#e5e7eb', margin: '0 4px' }} />
        <button type="button" onClick={() => editor.chain().focus().undo().run()} style={btn(false)} title="Undo">
          Undo
        </button>
        <button type="button" onClick={() => editor.chain().focus().redo().run()} style={btn(false)} title="Redo">
          Redo
        </button>
      </div>
      <style>{`
        .letter-editor-surface table {
          border-collapse: collapse; width: 100%; table-layout: fixed; margin: 10px 0;
        }
        .letter-editor-surface th, .letter-editor-surface td {
          border: 1px solid #cbd5e1; padding: 5px 7px; vertical-align: top;
          position: relative; word-wrap: break-word; font-size: 13px;
        }
        .letter-editor-surface th { background: #eef2f6; font-weight: 700; text-align: left; }
        .letter-editor-surface th p, .letter-editor-surface td p { margin: 0; }
        .letter-editor-surface .selectedCell:after {
          content: ''; position: absolute; inset: 0;
          background: rgba(37, 99, 235, .12); pointer-events: none;
        }
        /* Unfilled template placeholders. Decoration only — never saved into
           the letter body, so a printed letter shows plain text. */
        .letter-editor-surface .letter-placeholder {
          background: #fef3c7; border-bottom: 1px dashed #d97706;
          border-radius: 2px; padding: 0 1px;
        }
      `}</style>
      <div className="letter-editor-surface">
        <EditorContent editor={editor} placeholder={placeholder} />
      </div>
    </div>
  );
}

export default LetterEditor;
